import React, { useState, useEffect, useRef, useCallback } from "react";
import { AiOutlinePlus, AiOutlineClose } from "react-icons/ai";
import {
    getOperatorsForField as getOperators,
    getInputCount as getCount,
    getFieldControl,
    findField,
    groupFields,
} from "../config/searchFields";

/**
 * AdvancedFilterBuilder - Componente para construir filtros con grupos y lógica OR
 *
 * Permite crear múltiples grupos de filtros, cada uno con su propia lógica interna (AND/OR),
 * y combinar los grupos con un operador global (AND/OR).
 */
const AdvancedFilterBuilder = ({ metadataSpecs, onFiltersChange, initialGroups = [], onSuggest }) => {
    // Generar ID único
    const generateId = () => `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const [suggestions, setSuggestions] = useState({});
    const debounceTimers = useRef({});

    const fetchSuggestions = useCallback((conditionId, fieldName, value) => {
        if (!onSuggest || !fieldName || !value || value.length < 2) {
            setSuggestions(prev => ({ ...prev, [conditionId]: [] }));
            return;
        }
        if (debounceTimers.current[conditionId]) clearTimeout(debounceTimers.current[conditionId]);
        debounceTimers.current[conditionId] = setTimeout(async () => {
            try {
                const results = await onSuggest(fieldName, value);
                setSuggestions(prev => ({ ...prev, [conditionId]: results || [] }));
            } catch { setSuggestions(prev => ({ ...prev, [conditionId]: [] })); }
        }, 300);
    }, [onSuggest]);

    // Crear nuevo grupo vacío
    function createNewGroup() {
        return {
            id: generateId(),
            operator: "AND",
            conditions: []
        };
    }

    // Crear nueva condición vacía
    function createNewCondition() {
        const firstField = metadataSpecs[0];
        if (!firstField) return null;

        const operators = getOperatorsForField(firstField.name);
        const defaultOperator = operators[0]?.value || 'term';

        return {
            id: generateId(),
            fieldName: firstField.name,
            operator: defaultOperator,
            value: '',
            value2: ''
        };
    }

    const [filterGroups, setFilterGroups] = useState(initialGroups.length > 0 ? initialGroups : [createNewGroup()]);
    const [groupOperator, setGroupOperator] = useState("OR");

    // Sincronizar con initialGroups cuando cambia desde el padre (ej: reset)
    useEffect(() => {
        if (initialGroups.length === 0) {
            // Si el padre resetea a vacío, crear un grupo nuevo
            setFilterGroups([createNewGroup()]);
            setGroupOperator("OR");
        }
    }, [initialGroups]);

    // Operadores, tipos de input y controles viven en config/searchFields.js, compartidos
    // con FilterBuilder para que un tipo de campo nuevo se agregue en un solo lugar.
    const getOperatorsForField = (fieldName) => getOperators(metadataSpecs, fieldName);
    const getInputCount = (fieldName, operatorValue) => getCount(metadataSpecs, fieldName, operatorValue);

    // Notificar cambios al padre
    const notifyChange = (updatedGroups, updatedGroupOperator) => {
        onFiltersChange({
            filterGroups: updatedGroups,
            groupOperator: updatedGroupOperator
        });
    };

    // Agregar nuevo grupo
    const handleAddGroup = () => {
        const newGroup = createNewGroup();
        const updatedGroups = [...filterGroups, newGroup];
        setFilterGroups(updatedGroups);
        notifyChange(updatedGroups, groupOperator);
    };

    // Eliminar grupo
    const handleRemoveGroup = (groupId) => {
        const updatedGroups = filterGroups.filter(g => g.id !== groupId);
        setFilterGroups(updatedGroups);
        notifyChange(updatedGroups, groupOperator);
    };

    // Cambiar operador de un grupo
    const handleGroupOperatorChange = (groupId, newOperator) => {
        const updatedGroups = filterGroups.map(g =>
            g.id === groupId ? { ...g, operator: newOperator } : g
        );
        setFilterGroups(updatedGroups);
        notifyChange(updatedGroups, groupOperator);
    };

    // Agregar condición a un grupo
    const handleAddCondition = (groupId) => {
        const newCondition = createNewCondition();
        if (!newCondition) return;

        const updatedGroups = filterGroups.map(g =>
            g.id === groupId
                ? { ...g, conditions: [...g.conditions, newCondition] }
                : g
        );
        setFilterGroups(updatedGroups);
        notifyChange(updatedGroups, groupOperator);
    };

    // Eliminar condición de un grupo
    const handleRemoveCondition = (groupId, conditionId) => {
        const updatedGroups = filterGroups.map(g =>
            g.id === groupId
                ? { ...g, conditions: g.conditions.filter(c => c.id !== conditionId) }
                : g
        );
        setFilterGroups(updatedGroups);
        notifyChange(updatedGroups, groupOperator);
    };

    // Actualizar campo de una condición
    const handleFieldChange = (groupId, conditionId, newFieldName) => {
        const operators = getOperatorsForField(newFieldName);
        const defaultOperator = operators[0]?.value || 'term';

        const updatedGroups = filterGroups.map(g =>
            g.id === groupId
                ? {
                    ...g,
                    conditions: g.conditions.map(c =>
                        c.id === conditionId
                            ? { ...c, fieldName: newFieldName, operator: defaultOperator, value: '', value2: '' }
                            : c
                    )
                }
                : g
        );
        setFilterGroups(updatedGroups);
        notifyChange(updatedGroups, groupOperator);
    };

    // Actualizar operador de una condición
    const handleOperatorChange = (groupId, conditionId, newOperator) => {
        const updatedGroups = filterGroups.map(g =>
            g.id === groupId
                ? {
                    ...g,
                    conditions: g.conditions.map(c =>
                        c.id === conditionId
                            ? { ...c, operator: newOperator, value: '', value2: '' }
                            : c
                    )
                }
                : g
        );
        setFilterGroups(updatedGroups);
        notifyChange(updatedGroups, groupOperator);
    };

    // Actualizar valor de una condición
    const handleValueChange = (groupId, conditionId, valueKey, newValue, fieldName, fieldType) => {
        const updatedGroups = filterGroups.map(g =>
            g.id === groupId
                ? {
                    ...g,
                    conditions: g.conditions.map(c =>
                        c.id === conditionId
                            ? { ...c, [valueKey]: newValue }
                            : c
                    )
                }
                : g
        );
        setFilterGroups(updatedGroups);
        if (valueKey === 'value' && fieldType === 'STRING') {
            fetchSuggestions(conditionId, fieldName, newValue);
        }
        notifyChange(updatedGroups, groupOperator);
    };

    // Cambiar operador global entre grupos
    const handleGlobalOperatorChange = (newOperator) => {
        setGroupOperator(newOperator);
        notifyChange(filterGroups, newOperator);
    };

    return (
        <div style={styles.container}>
            {/* Grupos de filtros */}
            {filterGroups.map((group, groupIndex) => (
                <React.Fragment key={group.id}>
                    {/* Operador entre grupos (excepto antes del primero) */}
                    {groupIndex > 0 && (
                        <div style={styles.groupOperatorContainer}>
                            <select
                                value={groupOperator}
                                onChange={(e) => handleGlobalOperatorChange(e.target.value)}
                                style={styles.groupOperatorSelect}
                            >
                                <option value="AND">AND</option>
                                <option value="OR">OR</option>
                            </select>
                        </div>
                    )}

                    {/* Grupo */}
                    <div style={styles.group}>
                        <div style={styles.groupHeader}>
                            <div style={styles.groupTitle}>
                                <span style={styles.groupLabel}>Grupo {groupIndex + 1}</span>
                                <select
                                    value={group.operator}
                                    onChange={(e) => handleGroupOperatorChange(group.id, e.target.value)}
                                    style={styles.groupInternalOperator}
                                >
                                    <option value="AND">AND</option>
                                    <option value="OR">OR</option>
                                </select>
                            </div>
                            {filterGroups.length > 1 && (
                                <button
                                    onClick={() => handleRemoveGroup(group.id)}
                                    style={styles.removeGroupButton}
                                    title="Eliminar grupo"
                                >
                                    <AiOutlineClose />
                                </button>
                            )}
                        </div>

                        {/* Condiciones del grupo */}
                        {group.conditions.length > 0 ? (
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>Campo</th>
                                        <th style={styles.th}>Operador</th>
                                        <th style={styles.th}>Valor</th>
                                        <th style={styles.thActions}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {group.conditions.map((condition) => {
                                        const control = getFieldControl(metadataSpecs, condition.fieldName);
                                        const inputType = control.type;
                                        const inputCount = getInputCount(condition.fieldName, condition.operator);
                                        const operators = getOperatorsForField(condition.fieldName);
                                        const condFieldSpec = findField(metadataSpecs, condition.fieldName);
                                        const condFieldType = condFieldSpec?.type || '';

                                        return (
                                            <tr key={condition.id} style={styles.row}>
                                                {/* Campo */}
                                                <td style={styles.td}>
                                                    <select
                                                        value={condition.fieldName}
                                                        onChange={(e) => handleFieldChange(group.id, condition.id, e.target.value)}
                                                        style={styles.select}
                                                    >
                                                        {groupFields(metadataSpecs).map(group => (
                                                            <optgroup key={group.label} label={group.label}>
                                                                {group.fields.map(field => (
                                                                    <option key={field.name} value={field.name}>
                                                                        {field.label}
                                                                    </option>
                                                                ))}
                                                            </optgroup>
                                                        ))}
                                                    </select>
                                                </td>

                                                {/* Operador */}
                                                <td style={styles.td}>
                                                    <select
                                                        value={condition.operator}
                                                        onChange={(e) => handleOperatorChange(group.id, condition.id, e.target.value)}
                                                        style={styles.select}
                                                    >
                                                        {operators.map(op => (
                                                            <option key={op.value} value={op.value}>
                                                                {op.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </td>

                                                {/* Valor(es) */}
                                                <td style={styles.td}>
                                                    <div style={styles.valueContainer}>
                                                        {control.kind === 'select' ? (
                                                            <select
                                                                value={condition.value}
                                                                onChange={(e) => handleValueChange(group.id, condition.id, 'value', e.target.value, condition.fieldName, condFieldType)}
                                                                style={styles.select}
                                                                aria-label="Valor"
                                                            >
                                                                <option value="">Seleccioná…</option>
                                                                {control.options.map(opt => (
                                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                                ))}
                                                            </select>
                                                        ) : (
                                                            <input
                                                                type={inputType}
                                                                list={condFieldType === 'STRING' ? `suggest-adv-${condition.id}` : undefined}
                                                                value={condition.value}
                                                                onChange={(e) => handleValueChange(group.id, condition.id, 'value', e.target.value, condition.fieldName, condFieldType)}
                                                                style={styles.input}
                                                                placeholder={inputCount === 2 ? "Desde" : "Valor"}
                                                                autoComplete="off"
                                                            />
                                                        )}
                                                        {condFieldType === 'STRING' && suggestions[condition.id]?.length > 0 && (
                                                            <datalist id={`suggest-adv-${condition.id}`}>
                                                                {suggestions[condition.id].map(s => <option key={s} value={s} />)}
                                                            </datalist>
                                                        )}
                                                        {inputCount === 2 && (
                                                            <>
                                                                <span style={styles.separator}>a</span>
                                                                <input
                                                                    type={inputType}
                                                                    value={condition.value2}
                                                                    onChange={(e) => handleValueChange(group.id, condition.id, 'value2', e.target.value)}
                                                                    style={styles.input}
                                                                    placeholder="Hasta"
                                                                />
                                                            </>
                                                        )}
                                                    </div>
                                                </td>

                                                {/* Acciones */}
                                                <td style={styles.tdActions}>
                                                    <button
                                                        onClick={() => handleRemoveCondition(group.id, condition.id)}
                                                        style={styles.removeButton}
                                                        title="Eliminar condición"
                                                    >
                                                        <AiOutlineClose />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        ) : (
                            <div style={styles.emptyGroup}>
                                No hay condiciones en este grupo
                            </div>
                        )}

                        {/* Botón agregar condición */}
                        <button
                            onClick={() => handleAddCondition(group.id)}
                            style={styles.addConditionButton}
                            disabled={metadataSpecs.length === 0}
                        >
                            <AiOutlinePlus style={styles.buttonIcon} />
                            Agregar condición al grupo
                        </button>
                    </div>
                </React.Fragment>
            ))}

            {/* Botón agregar grupo */}
            <button
                onClick={handleAddGroup}
                style={styles.addGroupButton}
                disabled={metadataSpecs.length === 0}
            >
                <AiOutlinePlus style={styles.buttonIcon} />
                Agregar Grupo
            </button>
        </div>
    );
};

const styles = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px'
    },
    groupOperatorContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '10px 0'
    },
    groupOperatorSelect: {
        padding: '8px 16px',
        borderRadius: '4px',
        border: '2px solid #00468B',
        backgroundColor: '#fff',
        color: '#00468B',
        fontSize: '14px',
        fontWeight: 'bold',
        cursor: 'pointer',
    },
    group: {
        border: '2px solid #00468B',
        borderRadius: '8px',
        padding: '15px',
        backgroundColor: '#f8f9fa'
    },
    groupHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '15px'
    },
    groupTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
    },
    groupLabel: {
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#00468B',
    },
    groupInternalOperator: {
        padding: '4px 8px',
        borderRadius: '4px',
        border: '1px solid #00468B',
        backgroundColor: '#fff',
        color: '#00468B',
        fontSize: '12px',
        fontWeight: 'bold',
        cursor: 'pointer',
    },
    removeGroupButton: {
        backgroundColor: '#dc3545',
        color: '#fff',
        border: 'none',
        borderRadius: '4px',
        padding: '6px 8px',
        cursor: 'pointer',
        fontSize: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background-color 0.2s'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        backgroundColor: '#fff',
        border: '1px solid #ddd',
        borderRadius: '4px',
        marginBottom: '10px'
    },
    th: {
        backgroundColor: '#00468B',
        color: '#fff',
        padding: '10px',
        textAlign: 'left',
        fontWeight: 'bold',
        fontSize: '14px',
    },
    thActions: {
        backgroundColor: '#00468B',
        color: '#fff',
        padding: '10px',
        width: '50px',
    },
    row: {
        borderBottom: '1px solid #ddd'
    },
    td: {
        padding: '10px',
        verticalAlign: 'middle'
    },
    tdActions: {
        padding: '10px',
        textAlign: 'center',
        verticalAlign: 'middle'
    },
    select: {
        width: '100%',
        padding: '8px',
        borderRadius: '4px',
        border: '1px solid #ccc',
        fontSize: '14px',
    },
    valueContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    input: {
        flex: 1,
        padding: '8px',
        borderRadius: '4px',
        border: '1px solid #ccc',
        fontSize: '14px',
    },
    separator: {
        color: '#666',
        fontSize: '14px',
        fontWeight: 'bold'
    },
    removeButton: {
        backgroundColor: '#dc3545',
        color: '#fff',
        border: 'none',
        borderRadius: '4px',
        padding: '6px 8px',
        cursor: 'pointer',
        fontSize: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background-color 0.2s'
    },
    emptyGroup: {
        padding: '20px',
        textAlign: 'center',
        color: '#666',
        fontStyle: 'italic',
        backgroundColor: '#fff',
        border: '1px dashed #ccc',
        borderRadius: '4px',
        marginBottom: '10px'
    },
    addConditionButton: {
        backgroundColor: '#28a745',
        color: '#fff',
        padding: '8px 16px',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        transition: 'background-color 0.2s'
    },
    addGroupButton: {
        backgroundColor: '#00468B',
        color: '#fff',
        padding: '10px 20px',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        alignSelf: 'flex-start',
        transition: 'background-color 0.2s'
    },
    buttonIcon: {
        fontSize: '16px'
    }
};

export default AdvancedFilterBuilder;
